/**
 * Parse the localStorage mirror of `recordingStatus` and report whether a
 * recording is active. An absent value means no recording has ever run in
 * this browser, so it reads as `false`; only a CORRUPT value returns `null`,
 * telling the caller to fall back to in-memory state instead of silently
 * skipping the recording stop (CUS-1250).
 */
export function parseStoredIsRecording(raw: string | null): boolean | null {
  if (raw === null) return false;
  try {
    const parsed = JSON.parse(raw) as { isRecording?: unknown };
    return parsed?.isRecording === true;
  } catch {
    return null;
  }
}

/**
 * Copy text to clipboard with fallback for Document PiP windows.
 *
 * `navigator.clipboard.writeText()` requires the document to be focused,
 * which fails inside Document PiP windows. Falls back to the deprecated
 * `execCommand('copy')` which doesn't have this restriction.
 */
export async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Fallback for unfocused documents (e.g. Document PiP windows)
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
